import { useState } from "react";

import { labeebTierPath } from "../../api/labeeb";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { Card } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { WriteResult } from "../write/EffectTiming";
import { WriteFailure } from "../write/WriteFailure";

/** A tier id the API accepts: a letter, then up to 31 letters, digits or hyphens. */
const TIER_ID = /^[a-z][a-z0-9-]{0,31}$/;

/**
 * Create a tier that does not exist yet.
 *
 * `expected_version: 0` is how this API distinguishes create from update.
 * The id and the two limits are checked here so a 422 is not the first time
 * the operator hears about them.
 */
export function LabeebNewTier({ config, onCreated }: { config: AppConfig; onCreated: () => void }) {
  const write = useWrite(config);
  const [id, setId] = useState("");
  const [daily, setDaily] = useState("");
  const [monthly, setMonthly] = useState("");
  const [confirming, setConfirming] = useState(false);

  const dailyN = Number(daily);
  const monthlyN = Number(monthly);
  const idBad = id !== "" && !TIER_ID.test(id);
  const limitsTouched = daily !== "" || monthly !== "";
  const limitsOk =
    Number.isInteger(dailyN) && Number.isInteger(monthlyN) && dailyN >= 1 && dailyN <= monthlyN;
  const limitsBad = limitsTouched && !limitsOk;
  const ready = TIER_ID.test(id) && limitsOk;
  const busy = write.state.kind === "submitting";

  function submit(reason: string) {
    if (!ready) return;
    void write
      .submit(labeebTierPath(id), {
        method: "PATCH",
        body: JSON.stringify({
          expected_version: 0,
          reason,
          daily: dailyN,
          monthly: monthlyN,
        }),
      })
      .then((r) => {
        if (!r) return;
        onCreated();
        setId("");
        setDaily("");
        setMonthly("");
      });
    setConfirming(false);
  }

  return (
    <Card title="New tier" note="Creates the row at version 0. It does not change an existing tier.">
      <Gate need="quota:write">
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            Tier id
            <input
              aria-label="Tier id"
              className="w-64 rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
              value={id}
              onChange={(e) => setId(e.target.value)}
            />
          </label>
          {idBad && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Use a short lowercase id: a letter, then letters, digits or hyphens.
            </p>
          )}
          <label className="flex flex-col gap-1 text-sm">
            Daily messages
            <input
              aria-label="Daily messages"
              inputMode="numeric"
              className="w-40 rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
              value={daily}
              onChange={(e) => setDaily(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Monthly messages
            <input
              aria-label="Monthly messages"
              inputMode="numeric"
              className="w-40 rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
            />
          </label>
          {limitsBad && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Daily must be at least 1 and no more than monthly.
            </p>
          )}
          <Button disabled={!ready || busy} onClick={() => setConfirming(true)}>
            Create tier…
          </Button>
        </div>
      </Gate>

      {write.state.kind === "done" && (
        <div className="mt-2">
          <WriteResult title="Tier created" onDismiss={write.reset}>
            <p className="text-sm">It is version 1 now. Assigning a business to it is a separate change.</p>
          </WriteResult>
        </div>
      )}
      <WriteFailure state={write.state} onDismiss={write.reset} onReread={onCreated} />

      <ConfirmDialog
        open={confirming}
        title={`Create ${id}`}
        confirmLabel="Create"
        requireReason
        reasonHint="e.g. DA-1800, a pilot envelope"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
        effect="A new tier. No business moves onto it until one is assigned."
      >
        <p>
          <span className="ref">{id}</span> grants {dailyN} messages a day and {monthlyN} a month.
        </p>
      </ConfirmDialog>
    </Card>
  );
}
